import { useOutletContext, useParams } from 'react-router-dom'

import { GetFeatureAvailabilitiesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ExpensableResourceType, useExpensable } from 'modules/expenses/hooks/useExpensable'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { ExpensableDetail } from 'modules/expenses/types/ExpensableDetail'

import { useBudgets } from './useBudgets'
import { usePayroll } from './usePayroll'
import { ProjectWorker } from './useProjects'
import { useSpendingPolicies } from './useSpendingPolicies'

type WithLoading<T> = T & {
  loading: boolean
}

export interface ProjectWorkersExpensableDetail {
  projectWorkers: ProjectWorker[]
  loading: boolean
}

export const useExpensableDetail = (
  expensableId: string,
  canReimburseThroughPayroll: boolean,
  canUseSpendingPolicies: boolean,
  hasWorkflowSpendingPolicies: boolean
): WithLoading<ExpensableDetail> => {
  const [resourceTypeRaw, resourceId] = expensableId.split('-')

  let resourceType: ExpensableResourceType | undefined

  switch (resourceTypeRaw) {
    case 'expense':
      resourceType = ExpensableResourceType.Expense
      break
    case 'perdiem':
      resourceType = ExpensableResourceType.PerDiem
      break
    case 'mileage':
      resourceType = ExpensableResourceType.Mileage
      break
  }

  const { section, canSeeTransactions } = useOutletContext<RouteContextParams>()
  const { groupId } = useParams()
  const { employee, company } = useCurrentSession()

  const { data } = useQuery(GetFeatureAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      modules: [],
      ids: ['projects_management', 'projects_with_subprojects'],
      companyId: company.id,
    },
  })

  const projectsFeatureAvailabilities: FeatureAvailabilityType[] | undefined =
    data?.companies.featureAvailabilities

  const hasProjectsFeature: boolean =
    projectsFeatureAvailabilities?.every((feature) => feature.available) ?? false

  const {
    expensable,
    loading: loadingExpensable,
    refetch: refetchExpensable,
  } = useExpensable({
    id: Number(expensableId),
    companyId: company.id,
    resourceType: resourceType,
    resourceId: Number(resourceId),
    groupIds: groupId ? [Number(groupId)] : undefined,
    employeeId: isMyExpensesSection(section) ? employee?.id : undefined,
    includeManualDrafts: isMyExpensesSection(section),
    includeGrouped: true,
    includeAttachments: true,
    includeTransactions: canSeeTransactions,
    includeProjects: hasProjectsFeature,
    includeJournalEntries: true,
    includeCostCenterDistribution: true,
  })

  const { loading: loadingPayroll, ...payroll } = usePayroll(canReimburseThroughPayroll)
  const { budget, budgetAllocation, loading: loadingBudgets } = useBudgets(expensable)

  const category = expensable?.expense?.category || expensable?.mileage?.category
  const subcategory = expensable?.expense?.subcategory || expensable?.mileage?.subcategory

  const {
    spendingPoliciesResult,
    loading: loadingPolicies,
    refetch: refetchSpendingPolicyEvaluate,
  } = useSpendingPolicies({
    expensableId: expensable?.id,
    amount: Number(expensable?.reimbursableAmount ?? expensable?.amount),
    currency: expensable?.currency,
    category: category?.id,
    subcategory: subcategory || '',
    effectiveOn: expensable?.effectiveOn,
    companyId: company.id.toString(),
    employeeId: Number(expensable?.owner?.id),
    canUseSpendingPolicies,
    hasWorkflowSpendingPolicies,
  })

  const loading = loadingExpensable || loadingPayroll || loadingBudgets || loadingPolicies

  return {
    expensable,
    payroll,
    budget,
    budgetAllocation,
    spendingPoliciesResult,
    loading,
    refetchSpendingPolicyEvaluate,
    refetchExpensable,
  }
}
